/**
 * UI Store
 *
 * Manages global UI state such as sidebar, theme, notifications, etc.
 */
import { defineStore } from 'pinia';

export type ThemeMode = 'light' | 'dark' | 'system';

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  theme: ThemeMode;
  pageLoading: boolean;
  lastAuthorizedRoute: string | null;
}

export const useUIStore = defineStore('ui', {
  state: (): UIState => ({
    sidebarOpen: true,
    sidebarCollapsed: true,
    theme: 'system',
    pageLoading: false,
    lastAuthorizedRoute: null,
  }),

  getters: {
    effectiveTheme: (state): 'light' | 'dark' => {
      if (state.theme === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return state.theme;
    },
  },

  actions: {
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },

    closeSidebar() {
      this.sidebarOpen = false;
    },

    openSidebar() {
      this.sidebarOpen = true;
    },

    toggleSidebarCollapse() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    },

    setTheme(newTheme: ThemeMode) {
      this.theme = newTheme;
      this.applyTheme();
    },

    applyTheme() {
      const root = document.documentElement;
      if (this.effectiveTheme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    },

    setPageLoading(loading: boolean) {
      this.pageLoading = loading;
    },

    setLastAuthorizedRoute(path: string) {
      this.lastAuthorizedRoute = path;
    },

    getLastAuthorizedRoute(): string {
      return this.lastAuthorizedRoute || '/';
    },

    initializeFromViewport() {
      // Handle viewport changes
      const mediaQuery = window.matchMedia('(max-width: 768px)');
      if (mediaQuery.matches) {
        this.sidebarOpen = false;
      }
      this.applyTheme();
    },
  },

  // Only persist true UI preferences (theme, sidebar collapse) across sessions.
  // `lastAuthorizedRoute` is intentionally excluded — it contains navigation paths
  // that may embed resource IDs and should not survive beyond the current session.
  persist: { paths: ['sidebarCollapsed', 'theme'] },
});
